import React from 'react'

import { CompetitionStats } from '../../../types/competition'

interface CompetitionSummaryCardProps {
  stats: CompetitionStats
  onViewDetails?: () => void
  loading?: boolean
}

export default function CompetitionSummaryCard({
  stats,
  onViewDetails,
  loading = false,
}: CompetitionSummaryCardProps) {
  if (loading) {
    return (
      <div className="bg-gradient-to-br from-slate-900/80 via-slate-800/80 to-slate-900/80 rounded-2xl p-4 sm:p-5 border border-white/5 animate-pulse">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-slate-700 rounded-xl" />
          <div className="flex-1">
            <div className="h-4 bg-slate-700 rounded w-32 mb-2" />
            <div className="h-3 bg-slate-700 rounded w-20" />
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-16 bg-slate-700 rounded-xl" />
          ))}
        </div>
      </div>
    )
  }

  // Don't show card if no matches
  if (stats.totalMatches === 0) {
    return null
  }

  return (
    <div className="bg-gradient-to-br from-slate-900/80 via-slate-800/80 to-slate-900/80 rounded-2xl p-4 sm:p-5 border border-white/5 shadow-xl backdrop-blur-sm hover:border-white/10 transition-all duration-300 group">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-red-500/30 to-red-600/20 rounded-xl flex items-center justify-center shadow-lg shadow-red-500/10">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-bold text-white tracking-tight">
              Competições
            </h3>
            <p className="text-xs text-slate-400">
              {stats.totalMatches} {stats.totalMatches === 1 ? 'luta' : 'lutas'}
            </p>
          </div>
        </div>
        {onViewDetails && (
          <button
            onClick={onViewDetails}
            className="text-xs text-slate-400 hover:text-white transition-colors flex items-center gap-1 group-hover:text-red-400"
          >
            Ver mais
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3.5 w-3.5 transform group-hover:translate-x-0.5 transition-transform"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
        {/* Victories */}
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-1.5 sm:p-3 text-center overflow-hidden">
          <div className="text-lg sm:text-2xl font-black text-emerald-400">
            {stats.wins}
          </div>
          <div className="text-[8px] sm:text-[10px] text-emerald-300 uppercase tracking-tight sm:tracking-wider font-semibold truncate">
            Vitórias
          </div>
        </div>

        {/* Losses */}
        <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-1.5 sm:p-3 text-center overflow-hidden">
          <div className="text-lg sm:text-2xl font-black text-rose-400">
            {stats.losses}
          </div>
          <div className="text-[8px] sm:text-[10px] text-rose-300 uppercase tracking-tight sm:tracking-wider font-semibold truncate">
            Derrotas
          </div>
        </div>

        {/* Submissions */}
        <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-1.5 sm:p-3 text-center overflow-hidden">
          <div className="text-lg sm:text-2xl font-black text-violet-400">
            {stats.submissionWins}
          </div>
          <div className="text-[8px] sm:text-[10px] text-violet-300 uppercase tracking-tight sm:tracking-wider font-semibold truncate">
            Finaliz.
          </div>
        </div>

        {/* Win Rate */}
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-1.5 sm:p-3 text-center overflow-hidden">
          <div className="text-lg sm:text-2xl font-black text-amber-400">
            {stats.winRate.toFixed(0)}%
          </div>
          <div className="text-[8px] sm:text-[10px] text-amber-300 uppercase tracking-tight sm:tracking-wider font-semibold truncate">
            Taxa
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      {stats.totalMatches > 0 && (
        <div className="mt-3 h-1.5 bg-slate-700/50 rounded-full overflow-hidden flex">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-700"
            style={{
              width: `${(stats.wins / stats.totalMatches) * 100}%`,
            }}
          />
          <div
            className="h-full bg-gradient-to-r from-rose-500 to-rose-400 transition-all duration-700"
            style={{
              width: `${(stats.losses / stats.totalMatches) * 100}%`,
            }}
          />
          {stats.draws > 0 && (
            <div
              className="h-full bg-gradient-to-r from-slate-500 to-slate-400 transition-all duration-700"
              style={{
                width: `${(stats.draws / stats.totalMatches) * 100}%`,
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}
